/**
 * Token Explanation Modal
 *
 * Explains what tokens are and how the context window works
 */

import React from 'react';
import { X } from 'lucide-react';
import { Button } from './button';
import { Dialog } from './dialog';

export type TokenExplanationModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const TokenExplanationModal: React.FC<TokenExplanationModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      aria-labelledby="token-modal-title"
      className="flex flex-col max-h-[85vh] overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border/30">
        <h2 id="token-modal-title" className="text-lg font-semibold text-foreground">
          Understanding Tokens
        </h2>
        <Button onClick={onClose} variant="ghost" size="icon" aria-label="Close">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6 text-sm text-muted-foreground overflow-y-auto flex-1">
        {/* What are tokens? */}
        <div className="space-y-2">
          <h3 className="font-semibold text-base text-foreground">What are tokens?</h3>
          <p className="leading-relaxed">
            Tokens are small pieces of text that the AI uses to read and remember a conversation.
            They are not exactly words — sometimes one word is one token, sometimes it's a few
            tokens.
          </p>
        </div>

        <div className="border-t border-border/30" />

        {/* How this affects your chat */}
        <div className="space-y-3">
          <h3 className="font-semibold text-base text-foreground">How this affects your chat</h3>
          <p className="leading-relaxed">
            The AI can only "see" a limited amount of text at once. This includes:
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>your messages</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>AI responses</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>uploaded files</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>system instructions</span>
            </li>
          </ul>
          <p className="leading-relaxed pt-2">
            All of this together makes up the <strong>context window</strong>.
          </p>
        </div>

        <div className="border-t border-border/30" />

        {/* Token usage in this chat */}
        <div className="space-y-3">
          <h3 className="font-semibold text-base text-foreground">Token usage in this chat</h3>
          <p className="leading-relaxed">
            This bar shows how much of the available context is currently used by this chat.
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>
                <strong>Used tokens</strong> — text the AI is actively considering
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>
                <strong>Remaining tokens</strong> — how much more information can be added
              </span>
            </li>
          </ul>
          <p className="leading-relaxed pt-2">When the limit is reached, the AI may:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>forget earlier parts of the conversation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>stop accepting new large inputs</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>ask you to shorten or remove content</span>
            </li>
          </ul>
        </div>

        <div className="border-t border-border/30" />

        {/* Good to know */}
        <div className="space-y-3">
          <h3 className="font-semibold text-base text-foreground">Good to know</h3>
          <ul className="space-y-1.5 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>Longer conversations use more tokens</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>Large files (like CSVs or documents) can use a lot of tokens</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>Starting a new chat gives the AI a fresh context</span>
            </li>
          </ul>
        </div>

        <div className="border-t border-border/30" />

        {/* Why this exists */}
        <div className="space-y-3">
          <h3 className="font-semibold text-base text-foreground">Why this exists</h3>
          <p className="leading-relaxed">This helps you understand:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>how much information the AI is currently using</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>why certain inputs may be limited</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>when it's better to start a new conversation</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 p-6 border-t border-border/30">
        <Button onClick={onClose} variant="primary">
          Got it
        </Button>
      </div>
    </Dialog>
  );
};

TokenExplanationModal.displayName = 'TokenExplanationModal';
